import { useEffect, useMemo, useState, useContext } from 'react';
import { useRemote } from '../../../Utils/Utils';
import { Search } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { BannerContext } from '../../../InternalApiApp';
import Table from '../../../components/Table/Table';
import LoadingIcon from '../../Shared/LoadingIcon';
import AnalyticsExportButton from './AnalyticsExportButton';
import SimpleSearchFilter, { filterBy } from '../../../components/Input/SimpleSearchFilter';
import FlexWrapper from '../../FlexWrapper';
import { analyticsExportFilename } from '../AnalyticsUtils';
import moment from 'moment';
import TruncatedString from '../../Shared/TruncatedString';


const EntryExitPortfolios = ({ timeFrame, customeDate }) => {
    const [loading, setLoading] = useState(true)
    const [entryData, setEntryData] = useState([])
    const [exitData, setExitData] = useState([])
    const [entryHasSearchTerm, setEntryHasSearchTerm] = useState("")
    const [exitHasSearchTerm, setExitHasSearchTerm] = useState("")

    const { bannerAdditionalInfo } = useContext(BannerContext)
    const remote = useRemote();
    const history = useHistory()

    useEffect(() => {
        if (bannerAdditionalInfo && customeDate && customeDate.from && customeDate.to) {
            setLoading(true);

            const end = moment(customeDate.to).format('YYYY-MM-DD');
            const start = moment(customeDate.from).format('YYYY-MM-DD');

            const products = bannerAdditionalInfo?.platforms.map(platform =>
                platform.products.map(p => p.selected ? `product=${p.id}` : null)
                    .filter(e => e !== null)
                    .join("&")
            )

            const entryPromise = remote.get(`/analytics/aggregates/entry-points?${products ? products + "&" : ""}from=${start}&to=${end}&scope=Manager`)
            const exitPromise = remote.get(`/analytics/aggregates/exit-points?${products ? products + "&" : ""}from=${start}&to=${end}&scope=Manager`)

            Promise.all([entryPromise, exitPromise]).then(([entryRankings, exitRankings]) => {
                const sortedEntryRankings = entryRankings.length > 0 ? entryRankings.sort((a, b) => b.sessionCount - a.sessionCount) : [];
                const sortedExitRankings = exitRankings.length > 0 ? exitRankings.sort((a, b) => b.sessionCount - a.sessionCount) : [];
                const entryTotalSessions = entryRankings.length > 0 ? entryRankings.reduce((acc, curr) => acc + curr.sessionCount, 0) : 0;
                const exitTotalSessions = exitRankings.length > 0 ? exitRankings.reduce((acc, curr) => (acc + curr.sessionCount), 0) : 0;
                const rankedEntry = sortedEntryRankings.map((portfolio, idx) => ({
                    ...portfolio,
                    rank: idx + 1,
                    sharePercentage: entryTotalSessions > 0 ? ((portfolio.sessionCount / entryTotalSessions) * 100)/*.toFixed(2)*/ : '0.00'
                }))
                const rankedExit = sortedExitRankings.map((portfolio, idx) => ({
                    ...portfolio,
                    rank: idx + 1,
                    sharePercentage: exitTotalSessions > 0 ? ((portfolio.sessionCount / exitTotalSessions) * 100)/*.toFixed(2)*/ : '0.00'
                }))
                setEntryData(rankedEntry)
                setExitData(rankedExit)
                setLoading(false);
            })
        }
    }, [timeFrame, customeDate, bannerAdditionalInfo, remote]);

    const colWidths = ["60px", "auto", "90px", "90px"]
    const headers = [
        {
            text: "Rank",
            className: 'compact-header'
        },
        {
            text: "Portfolio Name",
            className: 'compact-header'
        },
        {
            text: "Sessions",
            className: 'text-align-right compact-header'
        },
        {
            text: "Share (%)",
            className: 'text-align-right compact-header'
        }
    ]


    function dataMap(data) {
        const mappedData = data && data.map((portfolio, idx) => {
            if (idx > 9) return null;
            return (
                <tr key={idx} className="cursor-pointer" onClick={() => history.push(`/analytics/portfolios/${portfolio.link}`)}>
                    <td style={{ fontSize: '0.85rem' }}>{portfolio.rank}</td>
                    <td style={{ fontSize: '0.85rem' }}><TruncatedString string={portfolio.name} /></td>
                    <td style={{ fontSize: '0.85rem', textAlign: 'right' }}>{portfolio.sessionCount}</td>
                    <td style={{ fontSize: '0.85rem', textAlign: 'right' }}>{portfolio.sharePercentage.toFixed(2)}</td>
                </tr>
            );
        })
        return mappedData;
    };

    function filter(dataToFilter, searchTerm) {
        if (searchTerm === null || searchTerm.trim().length === 0) {
            return dataToFilter
        }
        return dataToFilter.filter((datum) => filterBy(datum, "name", searchTerm))
    }

    const filteredEntryData = filter(entryData, entryHasSearchTerm)
    const filteredExitData = filter(exitData, exitHasSearchTerm)

    const exportHeaders = useMemo(() => [
        { label: 'Rank', key: 'rank' },
        { label: 'Portfolio Name', key: 'name' },
        { label: 'Sessions', key: 'sessionCount' },
        { label: 'Share (%)', key: 'sharePercentageFormatted' },
    ], []);

    const buildExportRows = (rows) => rows.map(r => ({
        rank: r.rank,
        name: r.name,
        sessionCount: r.sessionCount,
        sharePercentageFormatted: typeof r.sharePercentage === 'number' ? r.sharePercentage.toFixed(2) : r.sharePercentage,
    }));

    const entryExportData = useMemo(() => buildExportRows(entryData), [entryData]);
    const exitExportData = useMemo(() => buildExportRows(exitData), [exitData]);

    const entryFilename = useMemo(
        () => analyticsExportFilename('entry-portfolios', customeDate),
        [customeDate]
    );
    const exitFilename = useMemo(
        () => analyticsExportFilename('exit-portfolios', customeDate),
        [customeDate]
    );

    const entryDataToDisplay = !!filteredEntryData ? dataMap(filteredEntryData) : null
    const exitDataToDisplay = !!filteredExitData ? dataMap(filteredExitData) : null

    return (
        <>
            <div className='mb-s '>First and last portfolio touched in each session:</div>
            <FlexWrapper className='mb-s' justify="space-between" align="center">
                <p className="font-weight-semi-bold">Entry</p>
                <FlexWrapper gap="s">
                    <SimpleSearchFilter
                        placeholder={`Search portfolios`}
                        setSearchTerm={setEntryHasSearchTerm}
                        size="small"
                        width="l"
                        icon={<Search />}
                        searchTerm={entryHasSearchTerm}
                    />
                    <AnalyticsExportButton headers={exportHeaders} data={entryExportData} filename={entryFilename} loading={loading} />
                </FlexWrapper>
            </FlexWrapper>
            {loading
                ? <div style={{ height: 322, display: 'flex' }}><LoadingIcon centered /></div>
                : <Table
                    suggestionMessage
                    suggestionMessageText={"No sessions found for this period"}
                    smStyle={{ height: 322 }}
                    height={322}
                    rowSize="s"
                    colWidths={colWidths}
                    headers={headers}
                    dataMap={entryDataToDisplay} />
            }
            <FlexWrapper className='mb-s mt-m' justify="space-between" align="center">
                <p className="font-weight-semi-bold">Exit</p>
                <FlexWrapper gap="s">
                    <SimpleSearchFilter
                        placeholder={`Search portfolios`}
                        setSearchTerm={setExitHasSearchTerm}
                        size="small"
                        width="l"
                        icon={<Search />}
                        searchTerm={exitHasSearchTerm}
                    />
                    <AnalyticsExportButton headers={exportHeaders} data={exitExportData} filename={exitFilename} loading={loading} />
                </FlexWrapper>
            </FlexWrapper>
            {loading
                ? <div style={{ height: 322, display: 'flex' }}><LoadingIcon centered /></div>
                : <Table
                    suggestionMessage
                    suggestionMessageText={"No sessions found for this period"}
                    smStyle={{ height: 322 }}
                    height={322}
                    rowSize="s"
                    colWidths={colWidths}
                    headers={headers}
                    dataMap={exitDataToDisplay} />
            }
        </>
    )
}

export default EntryExitPortfolios